import { useDispatch, useSelector } from 'react-redux';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { useEffect, useMemo } from 'react';
import { BackLink, Message, PromotionBadge } from '@bwoty-web/ui-kit';

import FadeIn from '../utils/fadeIn';
import ErrorPopup from '../components/shared/errorPopup/ErrorPopup';
import PageHeading from '../components/shared/pageHeading/PageHeading';
import PriceSummary from '../components/shared/priceSummary/PriceSummary';
import PriceDetails from '../components/shared/priceDetails/PriceDetails';
import GlobalContext from '../context/globalContext';
import GalleryLoader from '../components/shared/loaders/GalleryLoader';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import FlightListItem from '../components/shared/flightListItem/FlightListItem';
import CompactHotelItem from '../components/shared/compactProductItem/CompactHotelItem';
import MessageContainer from '../components/shared/message/MessageContainer';
import HeadingContainer from '../components/shared/headingContainer/HeadingContainer';
import CompactCruiseItem from '../components/shared/compactProductItem/CompactCruiseItem';
import ComboSummaryBreadcrumbs from '../components/comboSummary/ComboSummaryBreadcrumbs';
import PackagePlusOtherOptions from '../components/comboSummary/PackagePlusOtherOptions';
import { texts } from '../assets/texts';
import { urlUtils } from '../utils/url';
import { logAddCruiseToCart } from '../utils/logUtils/ComboSummaryLogUtils';

import { addToCart, errorPopupToggled, getFlightTime, init } from '../redux/actions/comboSummaryActions';
import {
  selectComboSummary,
  selectDestinationContent,
  selectError,
  selectIsInitialized,
  selectLabels,
  selectMessage,
  selectSearchInformation,
  selectSiteSettings,
  selectUserId,
} from '../redux/selectors/comboSummary';

import './comboSummaryContainer.scss';

// No props, one field per `useSelector`.
const ComboSummaryContainer = () => {
  const dispatch = useDispatch();
  const comboSummary = useSelector(selectComboSummary);
  const destinationContent = useSelector(selectDestinationContent);
  const error = useSelector(selectError);
  const initialized = useSelector(selectIsInitialized);
  const labels = useSelector(selectLabels);
  const message = useSelector(selectMessage);
  const searchInformation = useSelector(selectSearchInformation);
  const siteSettings = useSelector(selectSiteSettings);
  const userId = useSelector(selectUserId);
  const isGlobetrotter = useIsGlobetrotter();
  const isStopOver = urlUtils.isStopOver();
  const isPackagePlus = urlUtils?.getTdmTypeFromUrl()?.toLowerCase() === 'pluspackage';

  useEffect(() => {
    dispatch(init(siteSettings, userId));
  }, [dispatch, siteSettings, userId]);

  const hasSessionId = !!urlUtils?.getSessionIdFromUrl();

  const globals = useMemo(
    () => ({
      siteSettings,
      labels,
    }),
    [siteSettings, labels],
  );

  if (!initialized) {
    if (hasSessionId) {
      return (
        <LoaderHeading
          heading={texts[siteSettings?.siteId]?.loaderHeadingShort}
          topMargin
        />
      );
    }

    return (
      <GalleryLoader
        heading={texts[siteSettings?.siteId]?.loaderHeading}
        imageList={destinationContent?.imageLists?.normal || []}
        isPackagePlus={isPackagePlus}
        siteSettings={siteSettings}
        tips={texts[siteSettings?.siteId]?.tips}
      />
    );
  }

  const { selectedCombination, loadingAddToCart } = comboSummary;

  const {
    cruise,
    extras,
    extrasNotAssociatedWithAnyHotel,
    flightMissmatch,
    hotels,
    passengerDetailsLink,
    priceBreakDown,
    transferListLink,
    transport,
  } = selectedCombination || {};

  const { outBoundFlights, homeBoundFlights } = transport || {};
  const outBoundRouteKeys = outBoundFlights?.map((flight) => flight?.bookingEngineKey) || [];
  const homeBoundRouteKeys = homeBoundFlights?.map((flight) => flight?.bookingEngineKey) || [];
  const routeKeys = [...new Set([...outBoundRouteKeys, ...homeBoundRouteKeys])];
  const priceSummaryLabels = {
    button: labels?.bookingButtonLabel,
    totalPrice: labels?.totalPrice,
    priceDetails: labels?.priceDetailsLinkLabel,
    couponInfo: labels?.couponInfo,
  };
  const productAndPaxInfo = `${labels?.productsIncludedParsed}, ${searchInformation?.passengerInfo}`;
  const nextStepLink = urlUtils.standaloneUrlHandler(cruise ? passengerDetailsLink : transferListLink);

  const promoLabel = extras?.length ? extras[0]?.promoLabel : cruise?.promoLabel;

  const renderPriceDetails = () => {
    return (
      <PriceDetails
        priceBreakDown={priceBreakDown}
        searchInformation={searchInformation}
        labels={labels?.priceDetails}
        isSummaryView
      />
    );
  };

  const isGlobeCruise = !!cruise && isGlobetrotter;

  const headingType = isPackagePlus ? 'h2' : 'h1';

  return (
    <GlobalContext.Provider value={globals}>
      <FadeIn>
        <div className="iw-combo-summary-container">
          {isGlobeCruise && (
            <ComboSummaryBreadcrumbs
              breadcrumbCruiseDetailsLabel={labels?.breadcrumbCruiseDetailsLabel}
              cruiseDetailsLink={urlUtils.standaloneUrlHandler(cruise?.cruiseDetailsLink)}
              header={labels?.header}
            />
          )}
          <MessageContainer
            message={message}
            user={userId}
            labels={labels}
            style={{ marginBottom: 20, marginTop: 20 }}
          />
          {flightMissmatch && (
            <Message
              messageType="alert"
              header={labels?.flightMismatchMessage}
              style={{ marginBottom: 'var(--space-500)' }}
            />
          )}
          {selectedCombination && (
            <div className="iw-combo-summary">
              <HeadingContainer>
                {promoLabel ? (
                  <div className="iw-combo-summary__promo">
                    <PageHeading headingType={headingType}>{labels?.header}</PageHeading>
                    <PromotionBadge
                      promotion={promoLabel?.text}
                      urgency={promoLabel?.urgency}
                      type={promoLabel?.promotionType}
                    />
                  </div>
                ) : (
                  <PageHeading headingType={headingType}>{labels?.header}</PageHeading>
                )}
                {!!cruise && !isGlobetrotter && (
                  <BackLink href={urlUtils.standaloneUrlHandler(cruise?.cruiseDetailsLink)}>
                    {labels.backToCruiseDetails}
                  </BackLink>
                )}
              </HeadingContainer>
              {urlUtils?.getRecommendedFromUrl() && labels?.messageText && (
                <div className="iw-combo-summary__section">
                  <Message
                    header={labels?.messageText}
                    variant="info"
                  />
                </div>
              )}
              {cruise && (
                <div className="iw-combo-summary__section">
                  <h2 className="iw-combo-summary__section-heading">{labels?.cruiseHeader}</h2>
                  <CompactCruiseItem
                    cruise={cruise}
                    labels={labels}
                    isSummary
                  />
                </div>
              )}
              {isPackagePlus && extrasNotAssociatedWithAnyHotel?.length > 0 && (
                <div className="iw-combo-summary__section">
                  <PackagePlusOtherOptions
                    headerText={labels?.otherOptionsNotAssociatedHotelHeader}
                    otherOptions={extrasNotAssociatedWithAnyHotel}
                  />
                </div>
              )}
              {hotels?.map((hotel) => {
                const destination = hotel?.geographical?.resort?.name || hotel?.geographical?.area?.name;

                return (
                  <div
                    className="iw-combo-summary__section"
                    key={hotel?.accomodationBookingKey}
                  >
                    <h2 className="iw-combo-summary__section-heading">
                      {destination ? `${labels?.hotelHeader} ${destination}` : labels?.hotelHeaderNoResort}
                    </h2>
                    <CompactHotelItem
                      hotel={hotel}
                      labels={labels}
                      isSummary
                    />
                    {isPackagePlus && hotel.hotelExtras.length > 0 && (
                      <PackagePlusOtherOptions
                        headerText={`${labels?.otherOptionsAssociatedWithHotelHeader} ${hotel?.name}`}
                        otherOptions={hotel.hotelExtras}
                      />
                    )}
                  </div>
                );
              })}
              <div className="iw-combo-summary__section">
                <h2 className="iw-combo-summary__section-heading">{labels?.flightHeader}</h2>
                <FlightListItem
                  flightInformation={transport}
                  searchInformation={searchInformation}
                  labels={labels}
                  getFlightTime={(...args) => dispatch(getFlightTime(...args))}
                  displaySettings={{
                    isSummaryView: true,
                    hidePriceDetails: true,
                    hideFlightTime: isStopOver,
                  }}
                />
              </div>
              <PriceSummary
                labels={priceSummaryLabels}
                productAndPaxInfo={productAndPaxInfo}
                totalPrice={priceBreakDown?.totalPrice}
                onNavigate={() => {
                  if (cruise) {
                    logAddCruiseToCart(selectedCombination, siteSettings?.siteId);
                  }
                  dispatch(addToCart(routeKeys, cruise?.cruiseKey, nextStepLink));
                }}
                priceDetails={renderPriceDetails()}
                discounted={priceBreakDown?.isDiscounted}
                loading={loadingAddToCart}
              />
              {error?.visible && (
                <ErrorPopup
                  closePopup={() => dispatch(errorPopupToggled({ visible: false }))}
                  labels={labels?.errors}
                  error={error}
                />
              )}
            </div>
          )}
        </div>
      </FadeIn>
    </GlobalContext.Provider>
  );
};

export default ComboSummaryContainer;
